import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Container,
  Grid2 as Grid,
  Paper,
  Stack,
  Typography,
} from '@common/components'
import { SidebarDashboard } from '../sidebar-dashboard'
import { DashboardTabs } from '../dashboard-tabs'
import { Outlet, useLocation, useParams, useNavigate } from 'react-router-dom'
import {
  FACULTY_ONBOARDING_ROUTE,
  FEEDBACK_ROUTE,
  NOTES_ROUTE,
  RESOURCES_ROUTE,
  SUMMARY_ROUTE,
  USAGE_ROUTE,
} from '@common/constants/routes'
import { useUser } from '@features/auth/hooks'
import { handleGravatar } from '@common/utils/gravatar'
import AdminDashboardLayout from '../admin-dashboard-layout'
import { TutorialVideoModal } from '@features/recordings/components/tutorial-video-modal'
import { useAppDispatch, useAppSelector } from '@common/hooks'
import {
  selectTutorialVideoModalOpen,
  setTutorialVideoModalOpen,
} from '@features/recordings/recordings.slice'
import { setActiveRole, selectActiveRole, selectTokens } from '@features/auth/auth-slice'
import { useEffect, useState } from 'react'
import { useClassroomGetCurrentClassroomQuery } from '@app/services/auto-generated/classroom'
import { CenterContainer } from '@common/containers'
import errorHandler from '@common/utils/error-handler'
import { hasZid } from '@common/utils/tokens'
import React from 'react'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import PrivacyModal from '@features/auth/components/privacy-modal'
import { useModulesGetUserModulesQuery } from '@app/services/auto-generated/modules'

const DashboardLayout = () => {
  const { isAdmin, isTeacher } = useUser()
  const tutorialVideoModalOpen = useAppSelector(selectTutorialVideoModalOpen)
  const {
    data: modules,
    isSuccess: isModulesSuccess,
    isLoading: isModulesLoading,
  } = useModulesGetUserModulesQuery()
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false)
  const activeRole = useAppSelector(selectActiveRole)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {
    data: classroom,
    isSuccess,
    isLoading,
    isError,
    error,
  } = useClassroomGetCurrentClassroomQuery()

  useEffect(() => {
    if (isTeacher && activeRole == null) {
      dispatch(setActiveRole('admin'))
      navigate(USAGE_ROUTE)
    }
  }, [])

  // Handle navigation before rendering content
  if (isSuccess && classroom?.length === 0) {
    navigate(FACULTY_ONBOARDING_ROUTE, { replace: true })
    return null
  }
  useEffect(() => {
    if (isModulesSuccess && modules?.optional.includes('PrivacyPolicyModule')) {
      setPrivacyModalOpen(true)
    }
  }, [isModulesSuccess])
  console.log(privacyModalOpen)
  return (
    <>
      {isLoading || isModulesLoading ? (
        <CenterContainer fullScreen>
          <CircularProgress />
        </CenterContainer>
      ) : isError ? (
        <CenterContainer fullScreen>
          <Alert severity='error' variant='outlined'>
            {errorHandler(error)}
          </Alert>
        </CenterContainer>
      ) : isSuccess && classroom?.length > 0 ? (
        <>
          {!isTeacher && privacyModalOpen ? (
            <PrivacyModal open={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} />
          ) : (
            <>
              {/* {isUNSW() ? <UnswBackground /> : null} */}
              <TutorialVideoModal
                open={tutorialVideoModalOpen}
                onClose={() => dispatch(setTutorialVideoModalOpen(false))}
              />
              {isAdmin ? <AdminDashboardLayout /> : <Dashboard />}
            </>
          )}
        </>
      ) : null}
    </>
  )
}

const Dashboard = () => {
  const location = useLocation()
  const { feedback_id } = useParams()
  const { user } = useUser()
  return (
    <Container maxWidth={'xl'} sx={{ mb: 6, mx: 'auto', mt: 1 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ display: { xs: 'flex', md: 'none' }, mb: 2 }}
      >
        <Paper
          sx={{
            padding: '15px',
            width: { xs: '100%', sm: '50%' },
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            borderRadius: '10px',
            height: { xs: '70px', sm: 'auto' },
            justifyContent: 'flex-start',
          }}
        >
          <Avatar
            sx={{
              '.MuiAvatar-root': {
                color: 'black',
              },
            }}
            src={handleGravatar(user?.image ?? '')}
          />
          <Stack>
            <Typography sx={{ ml: 1 }}>
              Welcome back{' '}
              <Box
                component='span'
                sx={{
                  fontWeight: 600,
                  maxWidth: { xs: '250px', sm: '170px' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block', // Make sure it stays inline
                  verticalAlign: 'bottom', // Align properly with the text
                }}
              >
                {user?.full_name ?? user?.username}
              </Box>
            </Typography>
          </Stack>
        </Paper>
        {/* <DisplayClassroom /> */}
      </Stack>
      <Grid container spacing={4} sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}>
        <Grid size={{ xs: 12, md: 3.5 }}>
          <SidebarDashboard />
        </Grid>
        <Grid size={{ xs: 12, md: 8.5 }}>
          <DashboardTabs
            value={
              location.pathname.startsWith(FEEDBACK_ROUTE) ||
              location.pathname.startsWith(SUMMARY_ROUTE)
                ? location.pathname.startsWith(FEEDBACK_ROUTE)
                  ? feedback_id
                    ? FEEDBACK_ROUTE + `/${feedback_id}`
                    : FEEDBACK_ROUTE
                  : feedback_id
                  ? SUMMARY_ROUTE + `/${feedback_id}`
                  : SUMMARY_ROUTE
                : location.pathname
            }
          />
          <Paper
            elevation={0}
            sx={{
              borderRadius: '20px',
              height: 'calc(100vh - 190px)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                p: location.pathname.startsWith(RESOURCES_ROUTE) ? 0 : 3,
                flex: 1,
                overflowY: location.pathname.startsWith(FEEDBACK_ROUTE) ? 'hidden' : 'auto',
                '::-webkit-scrollbar': {
                  height: '10px',
                  width: '7px',
                  backgroundColor: 'transparent',
                },
                '::-webkit-scrollbar-track': {
                  boxShadow: 'inset 0 0 6px rgba(80, 80, 80, 0.1)',
                },
                '::-webkit-scrollbar-thumb': {
                  borderRadius: '5px',
                  backgroundColor: 'rgba(30, 30, 30, 0.5)',
                },
              }}
            >
              <Box sx={{ height: '100%' }}>
                <Outlet />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}
export default DashboardLayout
